import React, { useEffect, useState } from 'react';
import './AdminOverview.css';
import StatTile from './StatTile';
import RefreshButton from './RefreshButton';
import SignupsChart from './charts/SignupsChart';
import PlanDistributionChart from './charts/PlanDistributionChart';
import TopCountriesChart from './charts/TopCountriesChart';
import SurveySourceChart from './charts/SurveySourceChart';
import { adminApi } from './utils/adminApi';
import { formatCurrency, formatDate } from './utils/format';

const getWithdrawalStatusColor = (status) => {
  switch (status) {
    case 'completed': return '#22c55e';
    case 'pending': return '#eab308';
    case 'on_hold': return '#f97316';
    case 'rejected':
    case 'cancelled': return '#ef4444';
    default: return '#6b7280';
  }
};

const getWithdrawalStatusText = (status) => {
  switch (status) {
    case 'completed': return 'Completed';
    case 'pending': return 'Pending';
    case 'on_hold': return 'On Hold';
    case 'rejected': return 'Rejected';
    case 'cancelled': return 'Cancelled';
    default: return status || 'Unknown';
  }
};

const AdminOverview = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalRevenue: 0,
    activeUsers: 0,
    totalCommissions: 0,
    totalReferrals: 0,
  });
  const [analytics, setAnalytics] = useState({ signupsByDay: [], planDistribution: [], topCountries: [], bannedCount: 0, suspendedCount: 0, adminCount: 0 });
  const [surveyBreakdown, setSurveyBreakdown] = useState({});
  const [withdrawals, setWithdrawals] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);

  useEffect(() => {
    fetchAll(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // initial=true shows the full loading state; refreshes swap data in place
  const fetchAll = async (initial = false) => {
    if (initial) setLoading(true);
    setError('');
    try {
      const [users, revenue, activePlans, commissions, userAnalytics, survey, withdrawalsRes, onlineRes] = await Promise.all([
        adminApi.getTotalUsers(),
        adminApi.getTotalRevenue(),
        adminApi.getActivePlans(),
        adminApi.getReferralCommissions(),
        adminApi.getUserAnalytics(),
        adminApi.getSurveyBreakdown(),
        adminApi.getWithdrawals(),
        adminApi.getActivityOnline().catch(() => ({ userCount: 0 })),
      ]);

      setStats({
        totalUsers: users.totalUsers || 0,
        totalRevenue: revenue.totalRevenue || 0,
        activeUsers: activePlans.activeUsers || 0,
        totalCommissions: commissions.totalCommissions || 0,
        totalReferrals: commissions.totalReferrals || 0,
      });
      setAnalytics(userAnalytics);
      setSurveyBreakdown(survey);
      setWithdrawals(withdrawalsRes.withdrawals || []);
      setOnlineCount(onlineRes.userCount || 0);
    } catch (err) {
      console.error('Error fetching admin overview:', err);
      setError(err.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  if (selectedWithdrawal) {
    return (
      <div>
        <div className="admin-subheader">
          <button className="admin-back-btn" onClick={() => setSelectedWithdrawal(null)}>← Back</button>
          <h2>Withdrawal Details</h2>
        </div>
        <div className="withdrawal-detail-grid">
          <div className="withdrawal-detail-box">
            <div className="detail-label">ID</div>
            <div className="detail-value">{selectedWithdrawal.id}</div>
          </div>
          <div className="withdrawal-detail-box">
            <div className="detail-label">Status</div>
            <div className="detail-value" style={{ color: getWithdrawalStatusColor(selectedWithdrawal.status) }}>
              {getWithdrawalStatusText(selectedWithdrawal.status)}
            </div>
          </div>
          <div className="withdrawal-detail-box">
            <div className="detail-label">Amount</div>
            <div className="detail-value">${selectedWithdrawal.amount || 0}</div>
          </div>
          <div className="withdrawal-detail-box">
            <div className="detail-label">Date</div>
            <div className="detail-value">{formatDate(selectedWithdrawal.createdAt)}</div>
          </div>
          <div className="withdrawal-detail-box">
            <div className="detail-label">User ID</div>
            <div className="detail-value" style={{ fontSize: '14px', wordBreak: 'break-all' }}>
              {selectedWithdrawal.userId || '-'}
            </div>
          </div>
          <div className="withdrawal-detail-box">
            <div className="detail-label">Blockchain</div>
            <div className="detail-value">{selectedWithdrawal.blockchain || '-'}</div>
          </div>
          <div className="withdrawal-detail-box">
            <div className="detail-label">Coin</div>
            <div className="detail-value">{selectedWithdrawal.coin || '-'}</div>
          </div>
          <div className="withdrawal-detail-box withdrawal-detail-wide">
            <div className="detail-label">Wallet Address</div>
            <div className="detail-value" style={{ fontSize: '14px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {selectedWithdrawal.address || '-'}
            </div>
          </div>
          {selectedWithdrawal.reason && (
            <div className="withdrawal-detail-box withdrawal-detail-wide">
              <div className="detail-label">Reason</div>
              <div className="detail-value">{selectedWithdrawal.reason}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-block-head" style={{ marginBottom: 10 }}>
        <span />
        <RefreshButton onRefresh={fetchAll} title="Refresh overview" />
      </div>
      <div className="admin-stats-grid">
        <StatTile label="Online Now" value={onlineCount} accent="#22c55e" />
        <StatTile label="Total Users" value={stats.totalUsers} />
        <StatTile label="Active Plans" value={stats.activeUsers} />
        <StatTile label="Total Revenue" value={formatCurrency(stats.totalRevenue)} />
        <StatTile label="Total Referrals" value={stats.totalReferrals} />
        <StatTile label="Referral Commissions" value={formatCurrency(stats.totalCommissions)} />
        <StatTile label="Admins" value={analytics.adminCount} accent="#9085e9" />
        <StatTile label="Banned" value={analytics.bannedCount} accent="#ef4444" />
        <StatTile label="Suspended" value={analytics.suspendedCount} accent="#eab308" />
      </div>

      <div className="admin-charts-grid">
        <div className="admin-chart-card">
          <h3>Signups (last 30 days)</h3>
          <SignupsChart data={analytics.signupsByDay} />
        </div>
        <div className="admin-chart-card">
          <h3>Plan Distribution</h3>
          <PlanDistributionChart data={analytics.planDistribution} />
        </div>
        <div className="admin-chart-card">
          <h3>Top Countries</h3>
          <TopCountriesChart data={analytics.topCountries} />
        </div>
        <div className="admin-chart-card">
          <h3>Survey Sources</h3>
          <SurveySourceChart data={surveyBreakdown} />
        </div>
      </div>

      <div className="admin-section">
        <h2>Withdrawal Requests</h2>
        <div className="withdrawals-list">
          {withdrawals.length === 0 ? (
            <div className="no-data">No withdrawal requests</div>
          ) : (
            withdrawals.map((w) => (
              <div key={w.id} className="withdrawal-card" onClick={() => setSelectedWithdrawal(w)}>
                <div className="withdrawal-card-row">
                  <div className="withdrawal-card-info">
                    <span className="withdrawal-card-label">Amount</span>
                    <span className="withdrawal-card-value">${w.amount || 0}</span>
                  </div>
                  <div className="withdrawal-card-info">
                    <span className="withdrawal-card-label">Status</span>
                    <span className="withdrawal-card-value" style={{ color: getWithdrawalStatusColor(w.status) }}>
                      {getWithdrawalStatusText(w.status)}
                    </span>
                  </div>
                </div>
                <div className="withdrawal-card-row">
                  <div className="withdrawal-card-info">
                    <span className="withdrawal-card-label">Date</span>
                    <span className="withdrawal-card-value">{formatDate(w.createdAt)}</span>
                  </div>
                  <div className="withdrawal-card-info">
                    <span className="withdrawal-card-label">Method</span>
                    <span className="withdrawal-card-value">{w.blockchain} {w.coin}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
